/**
 * Refurbishment for one lot: the saved personal budget and its stored estimate
 * (`property-refurbishment-v3`), the synchronous budget save, and the shared
 * operator evidence corrections (floor area, refurbishment facts). The save is
 * acknowledged only after the server has calculated and stored the result; reads
 * consume saved results and start no calculation.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { api, ApiError, data } from './http'
import { keys } from './queries'
import type { RefurbishmentPreference } from './types'

/* ---------- personal preference helpers ---------- */

export function parseRefurbishmentPreference(value: unknown): RefurbishmentPreference {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Refurbishment preference must be an object.')
  const raw = value as Record<string, unknown>
  const fields = raw.mode === 'none' ? ['mode'] : raw.mode === 'auto' ? ['mode', 'profile'] : raw.mode === 'fixed' ? ['mode', 'amountPence'] : raw.mode === 'rate' ? ['mode', 'ratePence', 'areaUnit'] : []
  if (!fields.length || Object.keys(raw).some(key => !fields.includes(key)) || fields.some(key => !(key in raw))) throw new Error('Refurbishment preference contains missing or unsupported fields.')
  if (raw.mode === 'none') return { mode: 'none' }
  if (raw.mode === 'auto') {
    if (raw.profile !== 'light' && raw.profile !== 'standard' && raw.profile !== 'full') throw new Error('Choose a Light, Standard or Full refurbishment scope.')
    return { mode: 'auto', profile: raw.profile }
  }
  if (raw.mode === 'fixed') {
    if (!Number.isSafeInteger(raw.amountPence) || Number(raw.amountPence) < 0) throw new Error('Enter a non-negative refurbishment amount in whole pence.')
    return { mode: 'fixed', amountPence: raw.amountPence as number }
  }
  if (!Number.isSafeInteger(raw.ratePence) || Number(raw.ratePence) <= 0 || (raw.areaUnit !== 'sqm' && raw.areaUnit !== 'sqft')) throw new Error('Enter a positive refurbishment rate and choose its area unit.')
  return { mode: 'rate', ratePence: raw.ratePence as number, areaUnit: raw.areaUnit }
}

/** Convert user-entered pounds without silently rounding fractional pence. */
export function poundsToPence(value: string): number | null {
  if (!/^\d+(?:\.\d{1,2})?$/.test(value.trim())) return null
  const [whole, fraction = ''] = value.trim().split('.')
  const pence = Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
  return Number.isSafeInteger(pence) ? pence : null
}

export function refurbishmentPreferenceLabel(value: RefurbishmentPreference): string {
  if (value.mode === 'none') return 'None'
  if (value.mode === 'auto') return `Auto · ${value.profile === 'light' ? 'Light' : value.profile === 'full' ? 'Full' : 'Standard'}`
  const amount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 2 }).format((value.mode === 'fixed' ? value.amountPence : value.ratePence) / 100)
  return value.mode === 'fixed' ? `Fixed · ${amount}` : `${amount}/${value.areaUnit === 'sqft' ? 'sq ft' : 'm²'}`
}

/* ---------- floor area units (presentation only; the server validates the canonical m² fact) ---------- */

export type FloorAreaUnit = 'sqm' | 'sqft'
export function floorAreaInSquareMetres(value: number, unit: FloorAreaUnit): number { return unit === 'sqft' ? value * 0.09290304 : value }
export function floorAreaInSquareFeet(valueSqm: number): number { return valueSqm / 0.09290304 }

/* ---------- contract ---------- */

export type RefurbishmentProfile = 'light' | 'standard' | 'full'

export interface RefurbishmentCosts { worksPence: number | null; heritagePremiumPence: number | null; contractorMarginPence: number | null; vatPence: number | null; contingencyPence: number | null; totalPence: number | null }

export interface RefurbishmentScenario {
  profile: RefurbishmentProfile
  status: 'ready' | 'requires_input' | 'invalid_input'
  requiredInputs: string[]
  areaSqm: number | null
  quantityBasis: 'measured' | 'estimated' | 'mixed' | null
  provenance: { areaSource: string | null; roomSource: string | null; roomsComplete: boolean; verifiedCurrentSchedule: boolean }
  costs: RefurbishmentCosts
  includedScope: string[]
  quantityAssumptions: string[]
  warnings: string[]
  omittedScopeCount: number
  omittedQuantityAssumptionCount: number
  omittedWarningCount: number
}

export interface PropertyRefurbishment {
  contractVersion: 'property-refurbishment-v3'
  propertyId: string
  state: 'ready' | 'pending' | 'failed'
  diagnosticCode: string | null
  selectedProfile: RefurbishmentProfile | 'none' | null
  userDefault: RefurbishmentPreference
  propertyOverride: RefurbishmentPreference | null
  effectivePreference: RefurbishmentPreference
  effectiveCostPence: number | null
  selectionSource: 'userDefault' | 'propertyOverride'
  selectionRevision: number
  selectedScenario: RefurbishmentScenario | null
  floorArea: { valueSqm: number | null; source: string | null; overridden: boolean } | null
  scenarios: Record<RefurbishmentProfile, RefurbishmentScenario | null> | null
  provenance: { modelVersion: string | null; modelHash: string | null; sourceVersion: string | null; settingsRevision: number | null; computedAt: string | null }
}

function mismatch(message: string): never { throw new ApiError(502, 'refurbishment_contract_mismatch', message) }
function record(value: unknown): Record<string, unknown> { if (!value || typeof value !== 'object' || Array.isArray(value)) mismatch('Expected a refurbishment object'); return value as Record<string, unknown> }
function oneOf<T extends string>(value: unknown, choices: readonly T[]): T { if (typeof value !== 'string' || !choices.includes(value as T)) mismatch('Unknown refurbishment vocabulary'); return value as T }
function nullableString(value: unknown): string | null { if (value === null || value === undefined) return null; if (typeof value !== 'string') mismatch('Expected text or null'); return value }
function nullableNumber(value: unknown, integer = false): number | null { if (value === null || value === undefined) return null; if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || (integer && !Number.isSafeInteger(value))) mismatch('Expected a non-negative number or null'); return value }
function requiredCount(value: unknown, field: string): number { const parsed = nullableNumber(value, true); if (parsed === null) mismatch(`Missing ${field}`); return parsed }
function bool(value: unknown): boolean { if (typeof value !== 'boolean') mismatch('Expected boolean'); return value }
function boundedStrings(value: unknown, field: string): string[] { if (!Array.isArray(value) || value.length > 32 || value.some((item) => typeof item !== 'string' || item.length > 240)) mismatch(`Expected bounded ${field}`); return value as string[] }
const PROFILES = ['light', 'standard', 'full'] as const

function scenario(value: unknown, profile: RefurbishmentProfile): RefurbishmentScenario {
  const raw = record(value)
  if (raw.profile !== profile) mismatch('Scenario profile does not match its key')
  const provenance = record(raw.provenance), rawCosts = record(raw.costs)
  const costs: RefurbishmentCosts = {
    worksPence: nullableNumber(rawCosts.worksPence, true), heritagePremiumPence: nullableNumber(rawCosts.heritagePremiumPence, true),
    contractorMarginPence: nullableNumber(rawCosts.contractorMarginPence, true), vatPence: nullableNumber(rawCosts.vatPence, true),
    contingencyPence: nullableNumber(rawCosts.contingencyPence, true), totalPence: nullableNumber(rawCosts.totalPence, true),
  }
  const status = oneOf(raw.status, ['ready', 'requires_input', 'invalid_input'] as const)
  const components = [costs.worksPence, costs.heritagePremiumPence, costs.contractorMarginPence, costs.vatPence, costs.contingencyPence]
  if (status === 'ready') {
    if (costs.totalPence === null || components.some((value) => value === null)) mismatch('Ready refurbishment costs must be complete')
    if (components.reduce<number>((sum, value) => sum + (value ?? 0), 0) !== costs.totalPence) mismatch('Refurbishment costs do not reconcile')
  } else if (costs.totalPence !== null || components.some((value) => value !== null)) mismatch('Unpriced refurbishment costs must remain null')
  return {
    profile, status, requiredInputs: boundedStrings(raw.requiredInputs, 'required inputs'), areaSqm: nullableNumber(raw.areaSqm),
    quantityBasis: raw.quantityBasis === null ? null : oneOf(raw.quantityBasis, ['measured', 'estimated', 'mixed'] as const),
    provenance: { areaSource: nullableString(provenance.areaSource), roomSource: nullableString(provenance.roomSource), roomsComplete: bool(provenance.roomsComplete), verifiedCurrentSchedule: bool(provenance.verifiedCurrentSchedule) },
    costs, includedScope: boundedStrings(raw.includedScope, 'included scope'), quantityAssumptions: boundedStrings(raw.quantityAssumptions, 'quantity assumptions'), warnings: boundedStrings(raw.warnings, 'warnings'),
    omittedScopeCount: requiredCount(raw.omittedScopeCount, 'omitted scope count'), omittedQuantityAssumptionCount: requiredCount(raw.omittedQuantityAssumptionCount, 'omitted quantity-assumption count'), omittedWarningCount: requiredCount(raw.omittedWarningCount, 'omitted warning count'),
  }
}

export function parsePropertyRefurbishment(value: unknown): PropertyRefurbishment {
  const raw = record(record(value).data)
  if (raw.contractVersion !== 'property-refurbishment-v3') mismatch('Unsupported refurbishment contract')
  const propertyId = nullableString(raw.propertyId)
  if (!propertyId) mismatch('Missing property identity')
  const provenance = record(raw.provenance)
  let floorArea: PropertyRefurbishment['floorArea'] = null
  if (raw.floorArea !== null) { const area = record(raw.floorArea); floorArea = { valueSqm: nullableNumber(area.valueSqm), source: nullableString(area.source), overridden: bool(area.overridden) } }
  let scenarios: PropertyRefurbishment['scenarios'] = null
  if (raw.scenarios !== null) { const rows = record(raw.scenarios); scenarios = { light: rows.light === null ? null : scenario(rows.light, 'light'), standard: rows.standard === null ? null : scenario(rows.standard, 'standard'), full: rows.full === null ? null : scenario(rows.full, 'full') } }
  const state = oneOf(raw.state, ['ready', 'pending', 'failed'] as const)
  const selectionRevision = requiredCount(raw.selectionRevision, 'selection revision')
  const selectedProfile = raw.selectedProfile === null ? null : oneOf(raw.selectedProfile, [...PROFILES, 'none'] as const)
  let userDefault: RefurbishmentPreference, propertyOverride: RefurbishmentPreference | null, effectivePreference: RefurbishmentPreference
  try {
    userDefault = parseRefurbishmentPreference(raw.userDefault)
    propertyOverride = raw.propertyOverride === null ? null : parseRefurbishmentPreference(raw.propertyOverride)
    effectivePreference = parseRefurbishmentPreference(raw.effectivePreference)
  } catch (error) { return mismatch(error instanceof Error ? error.message : 'Invalid personal refurbishment preference') }
  if (JSON.stringify(effectivePreference) !== JSON.stringify(propertyOverride ?? userDefault)) mismatch('Effective refurbishment preference does not match its personal source')
  const selectionSource = oneOf(raw.selectionSource, ['userDefault', 'propertyOverride'] as const)
  if ((propertyOverride === null) !== (selectionSource === 'userDefault')) mismatch('Refurbishment selection source is inconsistent')
  const expectedProfile = effectivePreference.mode === 'auto' ? effectivePreference.profile : effectivePreference.mode === 'none' ? 'none' : null
  if (selectedProfile !== expectedProfile) mismatch('Selected profile does not match the effective preference')
  const selectedScenario = raw.selectedScenario === null ? null : scenario(raw.selectedScenario, effectivePreference.mode === 'auto' ? effectivePreference.profile : mismatch('A custom budget must not borrow an auto scenario'))
  const effectiveCostPence = nullableNumber(raw.effectiveCostPence, true)
  // The state describes the effective private budget; source evidence stays independently nullable.
  if ((state === 'ready') !== (effectiveCostPence !== null)) mismatch('Refurbishment budget state does not match its cost')
  if (effectivePreference.mode === 'none' && effectiveCostPence !== 0) mismatch('No-refurbishment budget must be zero')
  if (effectivePreference.mode === 'fixed' && effectiveCostPence !== effectivePreference.amountPence) mismatch('Fixed refurbishment budget does not match its amount')
  if (effectivePreference.mode === 'auto') {
    const expected = scenarios?.[effectivePreference.profile] ?? null
    if (JSON.stringify(selectedScenario) !== JSON.stringify(expected) || effectiveCostPence !== (expected?.costs.totalPence ?? null)) mismatch('Selected automatic budget does not match its retained scenario')
  }
  return {
    contractVersion: 'property-refurbishment-v3', propertyId, state, diagnosticCode: nullableString(raw.diagnosticCode), selectedProfile,
    userDefault, propertyOverride, effectivePreference, effectiveCostPence,
    selectionSource, selectionRevision, selectedScenario, floorArea, scenarios,
    provenance: { modelVersion: nullableString(provenance.modelVersion), modelHash: nullableString(provenance.modelHash), sourceVersion: nullableString(provenance.sourceVersion), settingsRevision: nullableNumber(provenance.settingsRevision, true), computedAt: nullableString(provenance.computedAt) },
  }
}

/* ---------- hooks ---------- */

/** `['refurbishment']` is also invalidated when the user default changes in Settings. */
export const refurbishmentKeys = { property: (propertyId: string) => ['refurbishment', propertyId] as const }

/** Saved Financial views that read one lot's results; refreshed after an acknowledged write. */
export function refreshLotResults(client: QueryClient, propertyId: string) {
  return Promise.all([
    client.invalidateQueries({ queryKey: ['calculationCards'] }),
    client.invalidateQueries({ queryKey: ['propertyMarkers'] }),
    client.invalidateQueries({ queryKey: ['areaPotential', propertyId] }),
    client.invalidateQueries({ queryKey: ['dealsV2', 'propertyModels', propertyId] }),
    client.invalidateQueries({ predicate: (query) => query.queryKey[0] === 'dealsV2' && query.queryKey[1] === 'result' && query.queryKey[3] === propertyId }),
  ])
}

export function usePropertyRefurbishment(propertyId: string, enabled = true) {
  return useQuery({
    queryKey: refurbishmentKeys.property(propertyId),
    queryFn: async ({ signal }) => parsePropertyRefurbishment(await api<unknown>('refurbishment', { params: { propertyId }, signal })),
    enabled: enabled && propertyId.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
  })
}

export function useSetRefurbishmentProfile(propertyId: string) {
  const client = useQueryClient()
  const key = refurbishmentKeys.property(propertyId)
  return useMutation({
    mutationFn: async (input: { override: RefurbishmentPreference | null; expectedRevision: number }) => parsePropertyRefurbishment(await api<unknown>('putRefurbishment', { params: { propertyId }, body: input })),
    onMutate: async () => {
      await client.cancelQueries({ queryKey: key })
      // The budget and saved outputs keep their identities until the server acknowledges the override.
      return { previous: client.getQueryData<PropertyRefurbishment>(key) }
    },
    onSuccess: (saved) => {
      client.setQueryData(key, saved)
      void refreshLotResults(client, propertyId)
    },
    onError: (_error, _input, context) => {
      if (context?.previous) client.setQueryData(key, context.previous)
      // A lost response may follow a committed choice: reconcile, never retry the write.
      void client.invalidateQueries({ queryKey: key })
      void refreshLotResults(client, propertyId)
    },
  })
}

function refreshAfterEvidence(client: QueryClient, propertyId: string) {
  return Promise.all([
    client.invalidateQueries({ queryKey: refurbishmentKeys.property(propertyId) }),
    client.invalidateQueries({ queryKey: ['calculationVariables', propertyId] }),
    client.invalidateQueries({ queryKey: keys.property(propertyId) }),
    refreshLotResults(client, propertyId),
  ])
}

export interface FloorAreaOverrideReceipt { contractVersion: 'operator-property-input-v1'; propertyId: string; field: 'floorAreaSqm'; value: number | null; unit: string; cleared: boolean }

/** Shared operator correction of the property's floor area (m²); null clears the override. */
export function useFloorAreaOverride(propertyId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (value: number | null) => data<FloorAreaOverrideReceipt>(value === null
      ? await api('operatorOverrideRemove', { params: { propertyId, field: 'floorAreaSqm' } })
      : await api('operatorOverride', { params: { propertyId, field: 'floorAreaSqm' }, body: { value } })),
    onSuccess: async () => { await refreshAfterEvidence(client, propertyId) },
  })
}

export type RefurbishmentOverrideField = 'mainsGasAvailable' | 'retainBoiler' | 'retainConsumerUnit' | 'subjectListed' | 'conservationArea' | 'singleGlazing' | 'accessibleLoft' | 'noLoftInsulation' | 'mainBathroomSeparateShowerFeasible' | 'windowCount' | 'radiatorCount'

/** Shared operator corrections of refurbishment facts; null clears the prior assertion. */
export function useRefurbishmentOverride(propertyId: string) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ field, value }: { field: RefurbishmentOverrideField; value: boolean | number | null }) => value === null
      ? api('operatorOverrideRemove', { params: { propertyId, field } })
      : api('operatorOverride', { params: { propertyId, field }, body: { value } }),
    onSuccess: async () => { await refreshAfterEvidence(client, propertyId) },
  })
}
